"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/auth-context";
import { NotificationConnectionState, useNotificationStore } from "@/stores/useNotificationStore";
import { createRealtimeSocket, type RealtimeSocket } from "@/lib/realtimeSocket";
import { normalizeResourceEventPayload } from "@/lib/realtime/normalizeResourceEventPayload";
import { routeResourceEventToDomainStore } from "@/lib/realtime/resourceEventRouter";
import type { CNSNotification } from "@/models/cns";
import { useConversationLineageStore } from "@/stores/useConversationLineageStore";
import { useProjectDocumentStore } from "@/stores/useProjectDocumentStore";
import { useWorkspaceStore } from "@/stores/useWorkspaceStore";
import { humanizeEventType, notificationDisplayText } from "./notificationDisplay";
import { notificationRequiresWorkspaceRefresh } from "./workspaceMembershipNotifications";
import { connectorOAuthCoordinator } from "@/lib/oauth/connectorOAuthCoordinator";

/**
 * Owns both realtime lanes on one WebSocket: notifications (bell/inbox) and
 * resource-events (silent cache coherence). Mounted inside the auth provider
 * tree so the socket is gated by the SESSION cookie.
 *
 * On every transition to "open" (initial connect and reconnect), runs inbox
 * gap-fill and refetches loaded project document + conversation lineage caches.
 * Open-chat message gap-fill is owned by useConversationThreadSync via connection state.
 */
export function NotificationSocketProvider(t0) {
  const $ = _c(10);
  const {
    children
  } = t0;
  const {
    user,
    isAuthenticated
  } = useAuth();
  const fetchInitial = useNotificationStore(_temp);
  const applyServerPush = useNotificationStore(_temp2);
  const setConnectionState = useNotificationStore(_temp3);
  const reset = useNotificationStore(_temp4);
  const socketRef = useRef(null);
  const workspaceRefreshTimerRef = useRef(null);
  const previousStateRef = useRef("idle");
  let t1;
  let t2;
  if ($[0] !== applyServerPush || $[1] !== fetchInitial || $[2] !== isAuthenticated || $[3] !== reset || $[4] !== setConnectionState || $[5] !== user) {
    t1 = () => {
      if (!isAuthenticated || !user) {
        if (workspaceRefreshTimerRef.current) {
          clearTimeout(workspaceRefreshTimerRef.current);
          workspaceRefreshTimerRef.current = null;
        }
        if (socketRef.current) {
          socketRef.current.deactivate();
          socketRef.current = null;
        }
        previousStateRef.current = "idle";
        reset();
        return;
      }
      if (socketRef.current) {
        return;
      }
      const socket = createRealtimeSocket({
        onNotification: raw => {
          ;
          try {
            const payload = normalizeNotificationPayload(JSON.parse(raw));
            applyServerPush(payload);
            if (notificationRequiresWorkspaceRefresh(payload.eventType)) {
              if (workspaceRefreshTimerRef.current) {
                clearTimeout(workspaceRefreshTimerRef.current);
              }
              workspaceRefreshTimerRef.current = setTimeout(() => {
                workspaceRefreshTimerRef.current = null;
                useWorkspaceStore.getState().refreshWorkspaces().catch(_temp5);
              }, 400);
            }
            if (payload.priority === "HIGH") {
              toast(notificationDisplayText(payload), {
                description: humanizeEventType(payload.eventType)
              });
            }
          } catch (t3) {
            const e = t3;
            console.warn("[notifications] dropped malformed STOMP frame", e);
          }
        },
        onResourceEvent: _temp6,
        onState: next => {
          const previous = previousStateRef.current;
          previousStateRef.current = next;
          setConnectionState(next);
          if (next === "open" && previous !== "open") {
            if (previous !== "idle") {
              console.info("[realtime] reconnect detected, running gap-fill");
              connectorOAuthCoordinator.runGapFill().catch(_temp7);
            }
            fetchInitial();
            useProjectDocumentStore.getState().invalidateAllLoadedProjects();
            useConversationLineageStore.getState().invalidateAllLoadedConversations().catch(_temp8);
          }
        }
      });
      socketRef.current = socket;
      socket.activate();
      return () => {
        if (workspaceRefreshTimerRef.current) {
          clearTimeout(workspaceRefreshTimerRef.current);
          workspaceRefreshTimerRef.current = null;
        }
        socket.deactivate();
        socketRef.current = null;
      };
    };
    t2 = [isAuthenticated, user, fetchInitial, applyServerPush, setConnectionState, reset];
    $[0] = applyServerPush;
    $[1] = fetchInitial;
    $[2] = isAuthenticated;
    $[3] = reset;
    $[4] = setConnectionState;
    $[5] = user;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  useEffect(t1, t2);
  let t3;
  if ($[8] !== children) {
    t3 = <>{children}</>;
    $[8] = children;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
function _temp8() {}
function _temp7() {}
function _temp6(raw_0) {
  ;
  try {
    const payload_0 = normalizeResourceEventPayload(JSON.parse(raw_0));
    if (!payload_0) {
      console.warn("[resource-events] dropped malformed STOMP frame");
      return;
    }
    routeResourceEventToDomainStore(payload_0);
  } catch (t0) {
    const e_0 = t0;
    console.warn("[resource-events] dropped malformed STOMP frame", e_0);
  }
}
function _temp5() {}
function _temp4(s_2) {
  return s_2.reset;
}
function _temp3(s_1) {
  return s_1.setConnectionState;
}
function _temp2(s_0) {
  return s_0.applyServerPush;
}
function _temp(s) {
  return s.fetchInitial;
}
function normalizeNotificationPayload(payload: unknown): CNSNotification {
  if (!payload || typeof payload !== "object") {
    throw new Error("Invalid notification payload");
  }
  const record = payload as Record<string, unknown>;
  const canonicalId = asString(record.id) ?? asString(record.notificationPublicId);
  const canonicalEventType = asString(record.eventType) ?? asString(record.name);
  const canonicalPriority = asPriority(record.priority);
  const createdAt = asString(record.createdAt);
  const eventVersion = asPositiveInteger(record.eventVersion) ?? 1;
  if (!canonicalId || !canonicalEventType || !canonicalPriority || !createdAt) {
    throw new Error("Notification payload missing required fields");
  }
  return {
    id: canonicalId,
    eventVersion,
    recipientUserPublicId: asString(record.recipientUserPublicId),
    domainEventPublicId: asNullableString(record.domainEventPublicId),
    eventType: canonicalEventType,
    entityType: asNullableString(record.entityType),
    entityPublicId: asNullableString(record.entityPublicId),
    workspacePublicId: asNullableString(record.workspacePublicId),
    projectPublicId: asNullableString(record.projectPublicId),
    conversationPublicId: asNullableString(record.conversationPublicId),
    summary: asNullableString(record.summary),
    destination: asNullableString(record.destination),
    priority: canonicalPriority,
    initiatorUserPublicId: asNullableString(record.initiatorUserPublicId),
    event: asDomainEvent(record.event),
    seenAt: asNullableString(record.seenAt),
    readAt: asNullableString(record.readAt),
    createdAt
  };
}
function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() !== "" ? value : undefined;
}
function asNullableString(value: unknown): string | null | undefined {
  if (value == null) return value as null | undefined;
  return asString(value);
}
function asPriority(value: unknown): CNSNotification["priority"] | undefined {
  return value === "LOW" || value === "MEDIUM" || value === "HIGH" ? value : undefined;
}
function asDomainEvent(value: unknown): CNSNotification["event"] {
  if (value == null) return value as null | undefined;
  if (typeof value === "object") return value as CNSNotification["event"];
  return undefined;
}
function asPositiveInteger(value: unknown): number | undefined {
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : undefined;
}